import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { Grid } from '@mui/material';
import type { ParticipationType } from 'app/types';
import { FormikModal } from 'app/components';
import { InputFieldFactory } from 'app/components/FormikField';
import type { ParticipationFormValues, ReasonStart, ReasonEnd } from '../../../../EmployeeIncomeParticipations.types';
import {
  ParticipationTranslationKeys,
  REASON_START_TRANSLATION_KEY,
  REASON_END_TRANSLATION_KEY,
  PARTICIPATION_TYPE_TRANSLATION_KEY,
  REASON_START_VALUES,
  REASON_END_VALUES,
} from '../../../../EmployeeIncomeParticipations.types';

type EmployeeIncomeParticipationFormDialogBodyProps = {
  open: boolean;
  isEdit: boolean;
  closeDialog: () => void;
  availableTypes: ParticipationType[];
};

export const EmployeeIncomeParticipationFormDialogBody = ({
  open,
  isEdit,
  closeDialog,
  availableTypes,
}: EmployeeIncomeParticipationFormDialogBodyProps) => {
  const intl = useIntl();
  const { submitForm, resetForm } = useFormikContext<ParticipationFormValues>();

  const onClickSubmit = React.useCallback(() => {
    submitForm();
  }, [submitForm]);

  const reasonStartItems = React.useMemo(() => {
    return REASON_START_VALUES.map((value: ReasonStart) => ({
      element: intl.formatMessage({ id: REASON_START_TRANSLATION_KEY[value] }),
      value,
    }));
  }, [intl]);

  const typeItems = React.useMemo(() => {
    return availableTypes.map(value => ({
      element: intl.formatMessage({ id: PARTICIPATION_TYPE_TRANSLATION_KEY[value] }),
      value,
    }));
  }, [availableTypes, intl]);

  const reasonEndItems = React.useMemo(() => {
    return REASON_END_VALUES.map((value: ReasonEnd) => ({
      element: intl.formatMessage({ id: REASON_END_TRANSLATION_KEY[value] }),
      value,
    }));
  }, [intl]);

  const createTitle = <FormattedMessage id={ParticipationTranslationKeys.dialogTitleCreate} />;
  const editTitle = <FormattedMessage id={ParticipationTranslationKeys.dialogTitleEdit} />;

  return (
    <FormikModal
      title={isEdit ? editTitle : createTitle}
      show={open}
      onSubmit={onClickSubmit}
      onCancel={closeDialog}
      onHide={() => {
        closeDialog();
        resetForm();
      }}
      disableEnforceFocus
    >
      {isEdit ? (
        <EmployeeIncomeParticipationEditForm
          typeItems={typeItems}
          reasonStartItems={reasonStartItems}
          reasonEndItems={reasonEndItems}
        />
      ) : (
        <EmployeeIncomeParticipationCreateForm
          typeItems={typeItems}
          reasonStartItems={reasonStartItems}
          availableTypes={availableTypes}
        />
      )}
    </FormikModal>
  );
};

type SelectItem = {
  element: string;
  value: string;
};

const EmployeeIncomeParticipationEditForm = ({
  typeItems,
  reasonStartItems,
  reasonEndItems,
}: {
  typeItems: SelectItem[];
  reasonStartItems: SelectItem[];
  reasonEndItems: SelectItem[];
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'date',
          header: ParticipationTranslationKeys.startDate,
          name: 'startDate',
          disabled: true,
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'select',
          header: ParticipationTranslationKeys.type,
          name: 'type',
          items: typeItems,
          disabled: true,
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'select',
          header: ParticipationTranslationKeys.reasonStart,
          name: 'reasonStart',
          items: reasonStartItems,
          disabled: true,
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'date',
          header: ParticipationTranslationKeys.endDate,
          name: 'endDate',
          required: true,
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'select',
          header: ParticipationTranslationKeys.reasonEnd,
          name: 'reasonEnd',
          items: reasonEndItems,
          required: true,
        }}
      />
    </Grid>
  </Grid>
);

const EmployeeIncomeParticipationCreateForm = ({
  typeItems,
  reasonStartItems,
  availableTypes,
}: {
  typeItems: SelectItem[];
  reasonStartItems: SelectItem[];
  availableTypes: ParticipationType[];
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'date',
          header: ParticipationTranslationKeys.startDate,
          name: 'startDate',
          required: true,
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'select',
          header: ParticipationTranslationKeys.reasonStart,
          name: 'reasonStart',
          items: reasonStartItems,
          required: true,
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <InputFieldFactory
        field={{
          type: 'select',
          header: ParticipationTranslationKeys.type,
          name: 'type',
          items: typeItems,
          required: availableTypes.length !== 1,
          disabled: availableTypes.length === 1,
        }}
      />
    </Grid>
  </Grid>
);
